import { isEqual as isDateEqual } from "date-fns";
// NOTE: Used to compare Date values reliably (ignores reference equality).
// TODO: Could use "src/helpers/dateUtils.js" instead for centralized date handling.

import { useCallback, useEffect, useMemo, useState } from "react";
import _get from "lodash/get";
import _set from "lodash/set";
import _cloneDeep from "lodash/cloneDeep";
import _isEqual from "lodash/isEqual";
import { isSameInstant } from "../../../helpers/dateUtils";

/**
 * Hook that manages editable state for an order and tracks "dirty" fields.
 *
 * Key design goals:
 * - Keep a deep-cloned immutable snapshot of the original order
 * - Allow granular field updates (including order rows)
 * - Track dirty state per field/path
 * - Automatically clear dirty state when a field reverts to its original value
 * - Produce a minimal payload containing only changed fields
 *
 * This hook is intentionally UI-agnostic.
 */
export function useEditableOrder(order) {

  /**
   * Deep clone of the incoming order.
   * This represents the immutable baseline used for dirty comparisons.
   *
   * Recomputed whenever `order` changes.
   */
  const original = useMemo(() => _cloneDeep(order || {}), [order]);

  /**
   * Mutable working copy used by the UI.
   */
  const [editableOrder, setEditableOrder] = useState(
    () => _cloneDeep(order || {})
  );

  /**
   * Set of dirty paths.
   *
   * Examples:
   * - "deliveryDateWish"
   * - "invoiceAddress.address1"
   * - "orderRows.<rowId>.confirmedDeliveryDate"
   *
   * A Set is used for O(1) lookups and easy add/remove semantics.
   */
  const [dirtyPaths, setDirtyPaths] = useState(() => new Set());

  console.log("** [useEditableOrder]", {
    order,
    original,
    editableOrder,
    dirtyPaths,
  }); // TODO: Remove debug logging

  /**
   * Reset editable state whenever a new order is selected.
   *
   * This ensures:
   * - No stale edits remain
   * - Dirty state always corresponds to the currently selected order
   */
  useEffect(() => {
    setEditableOrder(_cloneDeep(order || {}));
    setDirtyPaths(new Set());
  }, [order]);

  /**
   * Updates a single field and updates dirty state accordingly.
   *
   * @param {string} path - Path to the field (relative to order or row object)
   * @param {*} value - New value
   * @param {Object} [options]
   * @param {string|number} [options.rowId] - If provided, update an order row
   * @param {boolean} [options.isDate] - Enables date-aware equality comparison
   * @param {boolean} [options.section] - Override path (for addresses)
   * @param {boolean} [options.field] - Override path
   */
  const updateField = useCallback(
    (path, value, { rowId, isDate, section, field } = {}) => {

      const normalizedValue =
        isDate
          ? value
            ? value instanceof Date
              ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
              : value
            : null
          : value;

      setEditableOrder((prev) => {
        const updated = _cloneDeep(prev);

        // =====================================================
        // 1. ORDER ROWS (of�r�ndrad logik)
        // =====================================================
        if (rowId) {
          const rowIndex = updated.orderRows.findIndex((r) => r.id === rowId);
          if (rowIndex === -1) return updated;

          _set(updated.orderRows[rowIndex], path, normalizedValue);

          const originalRowIndex =
            original.orderRows?.findIndex((r) => r.id === rowId);

          const originalValue =
            originalRowIndex !== -1
              ? _get(original.orderRows[originalRowIndex], path)
              : undefined;

          const targetPath = `orderRows.${rowId}.${path}`;

          setDirtyPaths((prevDirty) => {
            const next = new Set(prevDirty);
            if (_isEqual(normalizedValue, originalValue)) next.delete(targetPath);
            else next.add(targetPath);
            return next;
          });

          return updated;
        }

        // =====================================================
        // 2. SORTED ADDRESS FIELDS (NY LOGIK)
        // =====================================================
        if (section && field) {
          const sectionFields = updated.sortedAddressFields?.[section];

          if (!sectionFields) return updated;

          const target = sectionFields.find(f => f.field === field);
          if (!target) return updated;

          const originalTarget =
            original.sortedAddressFields?.[section]
              ?.find(f => f.field === field);

          const originalValue = originalTarget?.value;

          target.value = normalizedValue;

          const targetPath = `${section}.${field}`;

          setDirtyPaths((prevDirty) => {
            const next = new Set(prevDirty);
            if (_isEqual(normalizedValue, originalValue)) next.delete(targetPath);
            else next.add(targetPath);
            return next;
          });

          return updated;
        }

        // =====================================================
        // 3. ROOT OBJECT (fallback � gamla strukturer)
        // =====================================================
        _set(updated, path, normalizedValue);

        const originalValue = _get(original, path);

        const isDirty =
          isDate
            ? normalizedValue !== originalValue
            : !_isEqual(normalizedValue, originalValue);

        setDirtyPaths((prevDirty) => {
          const next = new Set(prevDirty);
          const targetPath = path;

          if (isDirty) next.add(targetPath);
          else next.delete(targetPath);

          return next;
        });

        return updated;
      });
    },
    [original]
  );

  /**
   * Checks whether a specific field is dirty.
   *
   * @param {string} path
   * @param {Object} [options]
   * @param {string|number} [options.rowId]
   */
  const isFieldDirty = useCallback(
    (path, { rowId } = {}) => {
      const targetPath = rowId
        ? `orderRows.${rowId}.${path}`
        : path;
      return dirtyPaths.has(targetPath);
    },
    [dirtyPaths]
  );

  /**
   * Checks whether any field within an object path is dirty.
   *
   * Useful for:
   * - Address sections
   * - Collapsing / highlighting dirty groups
   *
   * Example:
   *   isObjectDirty("invoiceAddress")
   */
  const isObjectDirty = useCallback(
    (objectPath) => {
      for (const path of dirtyPaths) {
        if (path === objectPath || path.startsWith(objectPath + ".")) {
          return true;
        }
      }
      return false;
    },
    [dirtyPaths]
  );

  /**
   * Indicates whether *anything* has been modified.
   */
  const isDirty = useMemo(
    () => dirtyPaths.size > 0,
    [dirtyPaths]
  );

  /**
   * Builds a payload containing only changed fields.
   * - Top-level object fields are grouped normally
   * - orderRows are returned as an array of partial row objects:
   *   [{ id, ...changedFields }]
   */
  

  const getPayload = useCallback(() => {
    const payload = {};
    const orderRowsMap = new Map();

    dirtyPaths.forEach((path) => {
      const parts = path.split(".");

      // =====================================================
      // 1. ORDER ROWS
      // =====================================================
      if (parts[0] === "orderRows") {
        const [, rowId, ...fieldPath] = parts;
        if (!rowId || fieldPath.length === 0) return;

        const row = editableOrder.orderRows?.find(r => r.id === rowId);
        if (!row) return;

        if (!orderRowsMap.has(rowId)) {
          orderRowsMap.set(rowId, { id: rowId });
        }

        const rowPayload = orderRowsMap.get(rowId);
        const value = _get(row, fieldPath.join("."));

        _set(rowPayload, fieldPath.join("."), value);
        return;
      }

      // =====================================================
      // (m�ste �vers�tta t.ex. deliveryAddress.CustomerId till deliveryAddress.customerId)
      // =====================================================
      debugger;
      const addressParts = path.split(".");
      const section = addressParts[0];
      const field = addressParts[1];

      if (section && field && editableOrder.sortedAddressFields?.[section]) {
        const fieldObj = editableOrder.sortedAddressFields[section]
          .find(f => f.field === field);

        if (!fieldObj) return;
                
        const apiField = field.charAt(0).toLowerCase() + field.slice(1);

        if (!payload[section]) {
          payload[section] = {};
        }

        payload[section][apiField] = fieldObj.value;

        return;
      }

      // =====================================================
      // 3. FALLBACK vanliga f�lt
      // =====================================================
      const value = _get(editableOrder, path);
      _set(payload, path, value);
    });

    if (orderRowsMap.size > 0) {
      payload.orderRows = Array.from(orderRowsMap.values());
    }

    return payload;
  }, [dirtyPaths, editableOrder]);

  /**
   * Clears dirty state without touching editable values.
   * Typically called after a successful save.
   */
  const resetDirty = useCallback(
    () => setDirtyPaths(new Set()),
    []
  );

  return {
    editableOrder,
    updateField,
    isFieldDirty,
    isObjectDirty,
    isDirty,
    getPayload,
    resetDirty,
  };
}
